var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as R from 'ramda';
import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { AbstractFormErrorMessage } from '@axafr-btc-ds/_form-error-message';
import { cx } from '@emotion/css';
import { IconArrowDown } from '@axafr-btc-ds/icons';
import { Option, SelectLabel } from './select-option';
import { Typography } from '@axafr-btc-ds/typography';
import { Tooltip } from '@axafr-btc-ds/tooltip';
import { filterUseFormProps, FieldState } from '@axafr-btc-ds/form-validation';
import { wrapper } from './css';
import { BorderRadius } from '@axafr-btc-ds/layout';
import { v4 as uuidv4 } from 'uuid';
// eslint-disable-next-line max-lines-per-function,complexity
export var _Select = function (_a, ref) {
    var _b;
    var _c = _a.dataInput, dataInput = _c === void 0 ? [] : _c, errorMessage = _a.errorMessage, _d = _a.isDisabled, isDisabled = _d === void 0 ? false : _d, _e = _a.isRequired, isRequired = _e === void 0 ? false : _e, parentClassname = _a.parentClassname, labelClassName = _a.labelClassName, className = _a.className, onChange = _a.onChange, label = _a.label, name = _a.name, placeholder = _a.placeholder, _f = _a.fieldState, fieldState = _f === void 0 ? FieldState.INITIAL : _f, updateFieldState = _a.updateFieldState, _g = _a.defaultValue, defaultValue = _g === void 0 ? '' : _g, tooltipMessage = _a.tooltipMessage, tooltipDirection = _a.tooltipDirection, tooltipContainerBounds = _a.tooltipContainerBounds, value = _a.value, _h = _a.isReset, isReset = _h === void 0 ? false : _h, _j = _a.borderRadius, borderRadius = _j === void 0 ? BorderRadius.u0 : _j, props = __rest(_a, ["dataInput", "errorMessage", "isDisabled", "isRequired", "parentClassname", "labelClassName", "className", "onChange", "label", "name", "placeholder", "fieldState", "updateFieldState", "defaultValue", "tooltipMessage", "tooltipDirection", "tooltipContainerBounds", "value", "isReset", "borderRadius"]);
    var errorMessageId = uuidv4();
    var getOptionIndexFromValue = function (optionValue) { return dataInput.findIndex(function (option) { return option.value === optionValue; }); };
    var defaultOptionIndex = getOptionIndexFromValue(defaultValue);
    var selectRef = ref || useRef(null);
    var placeholderOption = { label: placeholder, subLabel: null, value: '' };
    var initialOption = defaultOptionIndex !== -1
        ? dataInput[defaultOptionIndex]
        : placeholderOption;
    var _k = useState(initialOption), currentOption = _k[0], setCurrentOption = _k[1];
    var isError = fieldState === FieldState.IS_ERROR;
    var updateCurrentOption = function (newValue) {
        var newOptionIndex = getOptionIndexFromValue(newValue);
        var hasNoOptionSelected = newOptionIndex === -1;
        setCurrentOption(hasNoOptionSelected ? placeholderOption : dataInput[newOptionIndex]);
    };
    var updateSelection = function (event) {
        var currentValue = event.target.value;
        updateCurrentOption(currentValue);
        updateFieldState === null || updateFieldState === void 0 ? void 0 : updateFieldState(event);
        onChange === null || onChange === void 0 ? void 0 : onChange(event);
    };
    var selectedValue = R.pathOr(defaultValue, ['current', 'value'], selectRef);
    useEffect(function () { return updateCurrentOption(selectedValue); }, [selectedValue]);
    useEffect(function () { return updateCurrentOption(initialOption.value); }, [isReset]);
    return (React.createElement("div", { className: cx(wrapper({ borderRadius: borderRadius }), parentClassname, { disabled: isDisabled }) },
        React.createElement(Typography, { variant: 'label1', component: "label", htmlFor: name, className: cx('axa-form-select-label', labelClassName) },
            label,
            !!tooltipMessage &&
                React.createElement(Tooltip, { className: "tooltip", direction: tooltipDirection, containerBounds: tooltipContainerBounds }, tooltipMessage)),
        React.createElement("div", { className: cx('axa-form-select-container', {
                'axa-form-select-container--no-option-selected': currentOption.value === placeholderOption.value,
                'axa-form-select-container--option-selected': !!currentOption.value,
                'axa-form-select-container--error': fieldState === FieldState.IS_ERROR,
                'axa-form-select-container--required': isRequired
            }, className) },
            React.createElement("div", { className: "axa-form-select-container-wrapper" },
                React.createElement("div", { className: "axa-form-select-container-text" },
                    React.createElement(SelectLabel, __assign({}, currentOption)),
                    React.createElement(IconArrowDown, { className: "axa-form-select-arrow-icon" }),
                    React.createElement("select", __assign({ 
                        // Note: Force re-render of the select to change the value when reset (Not testable with jest)
                        key: name + "-" + isReset, "aria-describedby": isError && errorMessageId, "aria-invalid": isError }, filterUseFormProps(props), { ref: selectRef, id: name, name: name, required: isRequired, disabled: isDisabled, defaultValue: value !== undefined ? undefined : defaultValue, value: value, onChange: updateSelection, className: cx('axa-form-select-tag', (_b = {},
                            _b["axa-form-select-tag--" + className] = !!className,
                            _b)) }),
                        !!placeholder && React.createElement(Option, { label: placeholder, value: "" }),
                        dataInput.map(function (optionData) { return (React.createElement(Option, __assign({ key: optionData.value }, optionData))); }))),
                React.createElement(AbstractFormErrorMessage, { id: errorMessageId, className: "formSelectErrorMessage", errorMessage: errorMessage, fieldState: fieldState, "aria-hidden": !isError })))));
};
export var Select = forwardRef(_Select);
